/** Formatting helpers shared across the views. */

export const clockTime = (value) =>
  new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** Inbox rows are one line: "14:02" today, "Yest", then a short date. */
export function shortTime(value) {
  if (!value) return '';
  const date = new Date(value);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return clockTime(date);

  const yesterday = new Date(now.getTime() - 86400000);
  if (date.toDateString() === yesterday.toDateString()) return 'Yest';

  return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

export function dayHeading(value) {
  const date = new Date(value);
  const now = new Date();
  const label = date
    .toLocaleDateString([], { day: 'numeric', month: 'long' })
    .toUpperCase();
  return date.toDateString() === now.toDateString() ? `TODAY · ${label}` : label;
}

/** "1m 08s" — the format the KPI tiles and per-agent tables use. */
export function duration(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${String(total % 60).padStart(2, '0')}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export const compact = (value) =>
  new Intl.NumberFormat([], { notation: 'compact', maximumFractionDigits: 1 }).format(value ?? 0);

export const number = (value) => new Intl.NumberFormat().format(value ?? 0);

export function percent(part, total) {
  if (!total) return '—';
  return `${((part / total) * 100).toFixed(1)}%`;
}

/* ------------------------------------------------------------ visitor time */

/**
 * The same instant, in the visitor's timezone.
 *
 * Everything else in this file renders in the *agent's* zone, because
 * `toLocaleTimeString` with no `timeZone` uses the browser's — which is
 * correct, and is what an agent reading a rail wants by default. What it
 * cannot tell them is whether it is the middle of the night where the visitor
 * is, and "they haven't replied in an hour" reads very differently at 03:00.
 *
 * Returns null for an empty or unrecognised zone, which is the signal to show
 * nothing rather than a wrong time — `session.timezone` is a client-reported
 * IANA name and a browser is free to send something we cannot resolve.
 */
export function inZone(value, timeZone) {
  if (!value || !timeZone) return null;
  try {
    return new Date(value).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    });
  } catch {
    return null;
  }
}

/** "now" where the visitor is — for the header line, not a stored timestamp. */
export const nowInZone = (timeZone) => inZone(Date.now(), timeZone);

/**
 * How far ahead or behind the visitor is, in hours: "+4.5", "-8", or "" when
 * they are in the same zone as the agent (in which case saying so is noise).
 */
export function zoneOffsetLabel(timeZone) {
  if (!timeZone) return '';
  try {
    const now = new Date();
    // Round-trip through a locale string in each zone and difference them. The
    // seconds are stripped on both sides, so a half-hour zone survives and a
    // sub-minute skew cannot produce a spurious offset.
    const there = new Date(now.toLocaleString('en-US', { timeZone }));
    const here = new Date(now.toLocaleString('en-US'));
    const hours = Math.round(((there - here) / 3_600_000) * 2) / 2;
    if (hours === 0) return '';
    return `${hours > 0 ? '+' : ''}${hours}h`;
  } catch {
    return '';
  }
}

/* ------------------------------------------------------------- user agent */

const BROWSERS = [
  // Order matters: every one of these also claims to be Chrome and Safari.
  [/Edg\/([\d.]+)/, 'Edge'],
  [/OPR\/([\d.]+)/, 'Opera'],
  [/SamsungBrowser\/([\d.]+)/, 'Samsung Internet'],
  [/Firefox\/([\d.]+)/, 'Firefox'],
  [/Chrome\/([\d.]+)/, 'Chrome'],
  [/Version\/([\d.]+).*Safari/, 'Safari'],
];

const PLATFORMS = [
  [/Windows NT 10/, 'Windows 10/11'],
  [/Windows NT/, 'Windows'],
  [/iPhone|iPad|iPod/, 'iOS'],
  [/Android[ /]([\d.]+)?/, 'Android'],
  [/Mac OS X/, 'macOS'],
  [/CrOS/, 'ChromeOS'],
  [/Linux/, 'Linux'],
];

/**
 * A user agent string, in words: "Chrome 121 · macOS · Desktop".
 *
 * Deliberately shallow. UA parsing is a losing game against a string every
 * browser lies in, so this covers what actually shows up in support and gives
 * up gracefully — the raw string stays one hover away, which is what an agent
 * debugging something odd needs anyway.
 */
export function describeUserAgent(ua) {
  if (!ua) return null;

  const browser = BROWSERS.reduce((found, [pattern, name]) => {
    if (found) return found;
    const match = ua.match(pattern);
    return match ? `${name} ${match[1].split('.')[0]}` : null;
  }, null);

  const platform = PLATFORMS.find(([pattern]) => pattern.test(ua))?.[1] ?? null;
  const mobile = /Mobile|Android|iPhone|iPad|iPod/.test(ua);

  const parts = [browser, platform, mobile ? 'Mobile' : 'Desktop'].filter(Boolean);
  return parts.length > 1 ? parts.join(' · ') : null;
}

/** Two-letter monogram for avatars: "Maya Kellner" -> "MK". */
export function initials(name, fallback = '?') {
  const source = (name ?? '').trim();
  if (!source) return fallback;
  const parts = source.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Stable per-app stripe colour, so an app looks the same in every view. */
export function appColor(appId, index) {
  if (typeof index === 'number' && index >= 0) return `var(--app-${(index % 6) + 1})`;
  let hash = 0;
  for (const char of String(appId)) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return `var(--app-${(hash % 6) + 1})`;
}

/** ISO date (YYYY-MM-DD) `days` ago — the shape the analytics range wants. */
export function daysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

export const STATUS_LABEL = {
  open: 'Open',
  pending: 'Pending',
  in_progress: 'In progress',
  waiting: 'Waiting',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const PRIORITY_LABEL = {
  urgent: 'P1',
  high: 'P2',
  normal: 'P3',
  low: 'P4',
};
